/**
 * Mobile menu accordion behaviour.
 *
 * `nav-item-mobile-accordion.liquid` renders nested <details> elements, which
 * open independently by default. This keeps a single panel open per nesting
 * level so the drawer never grows into a wall of links, and animates the panel
 * height because <details> has no transitionable height of its own.
 */
(() => {
  const ACCORDION_SELECTOR = '[data-dy-accordion]';
  const PANEL_SELECTOR = '.dy-accordion__panel';
  const OPEN_CLASS = 'is-animating-open';
  const TRANSITION_MS = 320;

  const panelOf = (details) => details.querySelector(`:scope > ${PANEL_SELECTOR}`);

  /** Siblings are the accordions sharing this one's immediate list container. */
  const siblingsOf = (details) => {
    const container = details.parentElement && details.parentElement.parentElement;
    if (!container) return [];

    return Array.from(container.children)
      .map((child) => child.querySelector(`:scope > ${ACCORDION_SELECTOR}`))
      .filter((sibling) => sibling && sibling !== details);
  };

  /*
   * A closed panel is `display: none` (see dy-pdp-tweaks.css), so it has no
   * height to animate from. Both directions therefore keep the panel displayed
   * for the length of the transition and only then hand control back to the
   * `open` attribute.
   */
  const collapse = (details) => {
    const panel = panelOf(details);
    if (!panel || !details.open) return;

    if (details.dataset.dyAnimating) return;
    details.dataset.dyAnimating = 'closing';

    panel.style.height = `${panel.scrollHeight}px`;
    // Force a reflow so the browser registers the start height before it changes.
    void panel.offsetHeight;
    panel.style.height = '0px';

    window.setTimeout(() => {
      details.open = false;
      details.classList.remove(OPEN_CLASS);
      panel.style.height = '';
      delete details.dataset.dyAnimating;
    }, TRANSITION_MS);
  };

  const expand = (details) => {
    const panel = panelOf(details);
    if (!panel) return;

    if (details.dataset.dyAnimating) return;
    details.dataset.dyAnimating = 'opening';

    // `open` must be set first, otherwise the panel is still display:none and
    // scrollHeight reads 0.
    details.open = true;
    details.classList.add(OPEN_CLASS);

    const target = panel.scrollHeight;
    panel.style.height = '0px';
    void panel.offsetHeight;
    panel.style.height = `${target}px`;

    window.setTimeout(() => {
      // Clearing the fixed height lets nested panels grow the parent later.
      panel.style.height = '';
      details.classList.remove(OPEN_CLASS);
      delete details.dataset.dyAnimating;
    }, TRANSITION_MS);
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.dy-accordion__summary');
    if (!summary) return;

    const details = summary.closest(ACCORDION_SELECTOR);
    if (!details) return;

    event.preventDefault();

    if (details.open) {
      collapse(details);
      return;
    }

    siblingsOf(details).forEach(collapse);
    expand(details);
  });

  /** Closing the drawer should reset the menu to its top level. */
  document.addEventListener('drawer:close', () => {
    document.querySelectorAll(`${ACCORDION_SELECTOR}[open]`).forEach((details) => {
      details.open = false;
      details.classList.remove(OPEN_CLASS);
      delete details.dataset.dyAnimating;
      const panel = panelOf(details);
      if (panel) panel.style.height = '';
    });
  });
})();
